import type { Operation, Server } from "@cloudserver/contracts";
import { Power } from "lucide-react";
import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { ApiProblem } from "../../../api/client.js";
import {
  runningByServer,
  useRunningOperations,
  useServer,
  useServerAction,
} from "../../../api/servers.js";
import { useReadOnly } from "../../../api/users.js";
import { kindOf, type TerminalTab } from "../../../terminal/store.js";
import { problemText } from "../../../ui/feedback.js";
import type { MenuItem } from "../../../ui/overlay.js";
import type { SessionTool } from "../../../ui/session.js";
import { useToast } from "../../../ui/toast.js";
import { PowerDialog, type DisruptiveAction } from "../power-dialog.js";
import { useOperationToasts } from "../watch.js";
import { vncConnectionOf } from "./connection.js";

/*
 * «Питание ▾» of a server's console (owner's request of 2026-10-22: a server
 * hung after a soft reboot, and Hetzner's reboot is an ACPI signal a stuck
 * system ignores — «Жёсткий перезапуск» must be one click away while
 * looking at the console). In the console's toolbar after «Настройки
 * сеанса» and in its tab's right-click menu; the same actions, dialogs and
 * operations as the server page's «Питание» tab (docs/console.md, "Power").
 */

export type ConsolePowerAction = "power-on" | DisruptiveAction;

/** The menu's order: on, soft off, hard off, soft reboot, hard reset. */
const ORDER: readonly ConsolePowerAction[] = [
  "power-on",
  "shutdown",
  "power-off",
  "reboot",
  "reset",
];

/** The key of an action's words in `sessions.power`. */
const WORDS: Record<ConsolePowerAction, string> = {
  "power-on": "powerOn",
  shutdown: "shutdown",
  "power-off": "powerOff",
  reboot: "reboot",
  reset: "reset",
};

/**
 * What fits the server's state: «Включить» when it is off; the two ways off
 * and the two restarts while it runs; nothing while it is on its way
 * (starting, stopping, rebuilding…).
 */
export function powerChoices(status: Server["status"] | undefined): ConsolePowerAction[] {
  if (status === "off") return ["power-on"];
  if (status === "running") return ORDER.filter((action) => action !== "power-on");
  return [];
}

/** Restarts after which the console is expected to drop and come back. */
const RESTARTS: readonly ConsolePowerAction[] = ["reboot", "reset", "power-on"];

export interface ConsolePower {
  /** «Питание ▾» for the toolbar; null where it does not apply (guests, not a console). */
  tool: SessionTool | null;
  /** The same items for the tab's menu. */
  items: MenuItem[];
  /** The confirmation and the operations' toasts: rendered once by the caller. */
  element: ReactNode;
  /** The operation running on the server now (its progress for the toolbar). */
  running: Operation | undefined;
}

/**
 * The power actions of a console tab (any other kind of tab: none).
 * Switching on runs at once; every action that interrupts the running
 * server asks first in the server page's `PowerDialog`. While an operation
 * runs on the server, the items wait for it. A reboot, reset or switching
 * on tells the console to come back by itself when it drops.
 */
export function useConsolePower(tab: TerminalTab): ConsolePower {
  const { t } = useTranslation();
  const toast = useToast();
  const isConsole = kindOf(tab) === "vnc";
  const { projectId, serverId } = tab;
  const server = useServer(projectId, serverId, isConsole).data;
  const running = runningByServer(useRunningOperations(projectId, isConsole).data).get(serverId);
  const readOnly = useReadOnly(projectId).readOnly;
  const run = useServerAction(projectId);
  const toasts = useOperationToasts(projectId);
  const [asking, setAsking] = useState<DisruptiveAction | null>(null);

  // Before the request: the server may drop the console before the answer comes back.
  const expectRestart = (action: ConsolePowerAction) => {
    if (RESTARTS.includes(action)) vncConnectionOf(tab.id)?.expectRestart();
  };
  const powerOn = () => {
    if (!server) return;
    expectRestart("power-on");
    run.mutate(
      { serverId: server.id, action: "power-on" },
      {
        onSuccess: (result) => {
          toast({
            tone: "info",
            title: t("servers.power.power-on.started", { name: server.name }),
          });
          toasts.watch(result.operation.id);
        },
        onError: (error) =>
          toast({
            tone: "danger",
            title:
              error instanceof ApiProblem && error.problem
                ? problemText(t, error.problem)
                : t("common.unexpected"),
          }),
      },
    );
  };

  const busy = !!running || !!server?.locked || run.isPending;
  const items: MenuItem[] =
    isConsole && server && !readOnly
      ? powerChoices(server.status).map((action) => ({
          label: t(`sessions.power.${WORDS[action]}`),
          ...(action === "reboot" ? { hint: t("sessions.power.rebootHint") } : {}),
          danger: action === "power-off" || action === "reset",
          disabled: busy,
          reason: t("servers.busy"),
          onSelect: () => (action === "power-on" ? powerOn() : setAsking(action)),
        }))
      : [];
  const tool: SessionTool | null =
    isConsole && server && !readOnly
      ? {
          id: "power",
          label: t("sessions.power.menu"),
          icon: Power,
          // On its way somewhere (starting, stopping): nothing to choose until it gets there.
          disabled: items.length === 0,
          items,
        }
      : null;
  const element = server ? (
    <>
      <PowerDialog
        projectId={projectId}
        server={server}
        action={asking}
        onClose={() => setAsking(null)}
        onConfirmed={expectRestart}
        onStarted={toasts.watch}
      />
      {toasts.element}
    </>
  ) : null;
  return { tool, items, element, running };
}
